import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GlassCard, Button, Icon, StatusPill, SearchInput, FilterSelect, Pagination } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { useAllVendors } from '../data/useAllVendors';
import { useVendorState, useLatestLinks, useRegistryStatus } from '../data/useVendorState';
import { RegistryNotice } from '../components/SelfRegularizationBits';
import { useShellToast } from '../useShellToast';
import { useAuth } from '../../../../contexts/AuthContext';
import { throughVendorService, getErrorMessage } from '../../../../services/apiService';
import { SEGMENTS, segmentByKey, segmentOf, LINK_VALID_DAYS, LINK_STATUS_PILL } from '../data/throughVendor';
import { ASKS, askByKey, defaultAsksFor, emailFor } from '../data/regularizationInsights';
import { CHECKS, checkByKey, toneFor } from '../data/selfRegularization';
import { VENDOR_STATUS_PILL, vendorPill } from '../data/messageStatus';
import { formatShortDate } from '../data/format';
import { VendorSheetUpload } from '../components/VendorSheetUpload';

/**
 * Send the link — three steps, the same shape as a verification run.
 *
 *   1. Vendors — who is asked. The real base, filtered, with what each record is missing.
 *   2. Ask     — what they are asked for. Each ask says how many of the chosen vendors need it.
 *   3. Review  — the exact email, who gets it, who cannot, and the confirmation before it goes.
 *
 * The rail on the right carries the running answer to "what am I about to do": how many vendors,
 * what they are asked for, how long it takes them, and what cannot be sent.
 */

const STEPS = [
  { n: '01', key: 'vendors', label: 'Vendors' },
  { n: '02', key: 'ask', label: 'What to ask' },
  { n: '03', key: 'review', label: 'Review & send' },
];
const PAGE_SIZE = 10;
const CHECKBOX = 'h-4 w-4 cursor-pointer rounded border-cos-line accent-[#0A9188]';
const num = (n) => Number(n || 0).toLocaleString('en-IN');
const MSME_LABEL = { MSME: 'MSME', NON_MSME: 'Non-MSME', PENDING: 'Pending' };

const NEED_FILTERS = [
  { key: 'all', label: 'Everyone' },
  { key: 'missing:any', label: 'Missing something on record' },
  ...CHECKS.map((c) => ({ key: `missing:${c.key}`, label: `No ${c.needsLabel}` })),
  { key: 'link:never', label: 'Never sent a link' },
  { key: 'link:expired', label: 'Link expired unanswered' },
];

function PageCheckbox({ all, some, onChange, disabled }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !all && some;
  }, [all, some]);
  return <input ref={ref} type="checkbox" className={CHECKBOX} checked={all} disabled={disabled} onChange={onChange} aria-label={all ? 'Untick this page' : 'Tick this page'} />;
}

/** What a record is missing, in the check's own colour. */
function GapChips({ inputs }) {
  const missing = CHECKS.filter((c) => !inputs.includes(c.needs));
  if (missing.length === 0) return <span className="text-2xs font-medium text-cos-teal-deep">Complete</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {missing.map((c) => {
        const t = toneFor(c.key);
        return (
          <span key={c.key} className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-2xs font-semibold ${t.bg} ${t.text}`} title={`No ${c.needsLabel}`}>
            <Icon name="remove" className="text-[12px]" weight={600} />
            {askByKey(c.key)?.short || c.label}
          </span>
        );
      })}
    </div>
  );
}

/** One ask, as a card: what it is, how long it takes, and how many of the chosen vendors need it. */
function AskCard({ ask, selected, needed, total, onToggle, disabled }) {
  const tone = toneFor(ask.key);
  const isWhole = ask.key === 'full';
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={selected}
      className={[
        'group flex w-full flex-col gap-2.5 rounded-2xl border p-4 text-left transition-all duration-200',
        selected ? `bg-white ${tone.ring}` : 'border-cos-line bg-white/55 hover:border-cos-teal/50 hover:bg-white/80',
        disabled ? 'cursor-not-allowed opacity-60' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${selected ? tone.solid : tone.icon}`}>
            <Icon name={ask.icon} className="text-[21px]" weight={500} />
          </span>
          <div className="min-w-0">
            <div className="text-[14px] font-bold tracking-tight text-cos-ink">{ask.label}</div>
            <div className="mono text-[12px] text-cos-ink-muted">about {ask.minutes} min</div>
          </div>
        </div>
        <span
          className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border transition ${
            selected ? `${tone.solid} border-transparent` : 'border-cos-line bg-white text-transparent group-hover:border-cos-teal/60'
          }`}
          aria-hidden="true"
        >
          <Icon name="check" className="text-[16px]" weight={700} />
        </span>
      </div>
      <p className="text-[12px] leading-[18px] text-cos-ink-secondary">{ask.blurb}</p>
      {!isWhole && (
        <p className="text-[12px] font-medium text-cos-ink">
          {needed === 0 ? (
            <span className="text-cos-ink-muted">None of the chosen vendors are missing this</span>
          ) : (
            <>
              <span className="mono">{num(needed)}</span> of {num(total)} chosen {needed === 1 ? 'vendor is' : 'vendors are'} missing it
            </>
          )}
        </p>
      )}
    </button>
  );
}

export function ThroughVendorWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useShellToast();
  const { isAdmin } = useAuth();

  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const all = useAllVendors();
  const base = all.vendors || [];
  const byId = useMemo(() => new Map(base.map((v) => [v.id, v])), [base]);

  const registry = useRegistryStatus();
  const vendorState = useVendorState();
  const links = useLatestLinks();
  const stateOf = vendorState.byId;
  const linkOf = links.byId;

  const [search, setSearch] = useState('');
  const [need, setNeed] = useState(params.get('need') && NEED_FILTERS.some((f) => f.key === params.get('need')) ? params.get('need') : 'all');
  const [segment, setSegment] = useState('All');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Map());
  const [asks, setAsks] = useState(() => new Set(params.get('ask')?.split(',').filter((k) => askByKey(k)) || []));
  const [sheet, setSheet] = useState(null);

  // Arriving from a suggestion or a run: the vendors and the ask are already decided.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !base.length) return;
    prefilled.current = true;
    const ids = (params.get('vendors') || '').split(',').map(Number).filter((n) => n > 0);
    if (ids.length) {
      const next = new Map();
      for (const id of ids) if (byId.has(id)) next.set(id, byId.get(id));
      setSelected(next);
      if (next.size > 0) setStep(asks.size > 0 ? 2 : 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return base.filter((v) => {
      if (segment !== 'All' && segmentOf(v) !== segment) return false;
      if (need !== 'all') {
        const [kind, arg] = need.split(':');
        const inputs = stateOf.get(v.id)?.inputs || [];
        if (kind === 'missing') {
          if (arg === 'any' ? CHECKS.every((c) => inputs.includes(c.needs)) : inputs.includes(checkByKey(arg)?.needs)) return false;
        }
        if (kind === 'link') {
          const link = linkOf.get(v.id);
          if (arg === 'never' ? !!link : link?.status !== arg) return false;
        }
      }
      if (!q) return true;
      return [v.company_name, v.vendor_code, v.email].some((s) => String(s || '').toLowerCase().includes(q));
    });
  }, [base, search, need, segment, stateOf, linkOf]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const rows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggle = (v) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(v.id)) next.delete(v.id);
      else next.set(v.id, v);
      return next;
    });
  const pageAll = rows.length > 0 && rows.every((v) => selected.has(v.id));
  const pageSome = rows.some((v) => selected.has(v.id));
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (pageAll) rows.forEach((v) => next.delete(v.id));
      else rows.forEach((v) => next.set(v.id, v));
      return next;
    });
  const selectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      filtered.forEach((v) => next.set(v.id, v));
      return next;
    });

  const onSheet = (res) => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const m of res.matched) next.set(m.id, byId.get(m.id) || m);
      return next;
    });
    setSheet(res);
    toast(`${num(res.matched.length)} of ${num(res.total_codes)} codes ticked${res.missing.length ? ` · ${num(res.missing.length)} unknown` : ''}`, {
      tone: res.missing.length ? 'warning' : 'success',
    });
  };

  const selectedRows = Array.from(selected.values());
  /** How many of the chosen vendors are actually missing each thing — the reason to ask for it. */
  const neededPerAsk = useMemo(() => {
    const counts = {};
    for (const c of CHECKS) {
      counts[c.key] = selectedRows.filter((v) => !(stateOf.get(v.id)?.inputs || []).includes(c.needs)).length;
    }
    counts.documents = selectedRows.length;
    counts.full = selectedRows.length;
    return counts;
  }, [selectedRows, stateOf]);

  const toggleAsk = (key) => {
    setConfirmed(false);
    setAsks((prev) => {
      const next = new Set(prev);
      if (key === 'full') return next.has('full') ? new Set() : new Set(['full']);
      next.delete('full');
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  /** "Ask each vendor only for what they are missing" is the common case — one click sets it. */
  const askWhatIsMissing = () => {
    setConfirmed(false);
    setAsks(new Set(CHECKS.filter((c) => neededPerAsk[c.key] > 0).map((c) => c.key)));
  };

  const askKeys = ASKS.filter((a) => asks.has(a.key)).map((a) => a.key);
  const withoutEmail = selectedRows.filter((v) => !v.email).length;
  const sendable = selectedRows.length - withoutEmail;
  const bySegment = useMemo(() => {
    const c = { recent: 0, legacy: 0 };
    for (const v of selectedRows) c[segmentOf(v)] += 1;
    return c;
  }, [selectedRows]);
  const majority = bySegment.legacy > bySegment.recent ? 'legacy' : 'recent';
  const email = useMemo(() => emailFor(majority, askKeys.length ? askKeys : defaultAsksFor(majority)), [majority, askKeys.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const canContinue = (step === 0 && selected.size > 0) || (step === 1 && askKeys.length > 0);
  const canSend = step === 2 && isAdmin && confirmed && sendable > 0 && askKeys.length > 0 && !sending;

  usePageChrome(
    () => ({
      title: 'Send the link',
      subtitle: ['Choose who is asked', 'Choose what they are asked for', 'Check it, then send'][step],
      actions: (
        <Button variant="ghost" icon="close" onClick={() => navigate('/campaigning-tool/regularization')}>
          Cancel
        </Button>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step]
  );

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const res = await throughVendorService.sendLinks({
        vendor_ids: selectedRows.filter((v) => v.email).map((v) => v.id),
        segment: majority,
        asks: askKeys,
      });
      const d = res.data || {};
      const failed = Number(d.failed || 0) + Number(d.skipped || 0);
      toast(res.message || `Link sent to ${num(d.vendor_count)} vendor${d.vendor_count === 1 ? '' : 's'}`, { tone: failed ? 'warning' : 'success' });
      navigate('/campaigning-tool/regularization');
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not send the links', { tone: 'error' });
      setSending(false);
    }
  };

  const loading = (all.loading && !all.vendors) || vendorState.loading;

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="flex min-w-0 flex-col gap-4">
        <RegistryNotice status={registry} />
        <div className="flex flex-wrap items-center gap-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <button
                type="button"
                onClick={() => i < step && setStep(i)}
                disabled={i > step}
                className={[
                  'flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition',
                  i < step
                    ? 'cursor-pointer border-cos-teal/30 bg-cos-teal-faint/50 text-cos-teal-deep hover:bg-cos-teal-faint'
                    : i === step
                      ? 'border-cos-teal bg-white text-cos-ink shadow-cos-teal-glow'
                      : 'cursor-not-allowed border-cos-line bg-white/40 text-cos-ink-secondary',
                ].join(' ')}
              >
                {i < step ? <Icon name="check" className="text-[17px]" /> : <span className="mono text-[12px]">{s.n}</span>}
                <span>{s.label}</span>
              </button>
              {i < STEPS.length - 1 ? <Icon name="chevron_right" className="text-[18px] text-cos-ink-muted" /> : null}
            </React.Fragment>
          ))}
        </div>

        {/* ------------------------------------------------------------- 1 vendors */}
        {step === 0 && (
          <GlassCard padded={false}>
            <div className="flex flex-col gap-3 px-5 pt-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-[15px] font-bold tracking-tight text-cos-ink">Who gets the link?</h3>
                  <p className="mt-0.5 text-[12px] text-cos-ink-secondary">
                    Narrow to the ones that need it, then tick rows, take everyone that matches, or upload a sheet of codes.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mono rounded-full bg-cos-teal-faint px-2.5 py-1 text-[12px] font-semibold text-cos-teal-deep">{num(selected.size)} selected</span>
                  <Button variant="secondary" size="sm" icon="select_all" onClick={selectAll} disabled={loading || filtered.length === 0 || filtered.every((v) => selected.has(v.id))}>
                    Select all {num(filtered.length)}
                  </Button>
                  <Button variant="ghost" size="sm" icon="deselect" onClick={() => setSelected(new Map())} disabled={selected.size === 0}>
                    Clear
                  </Button>
                  <VendorSheetUpload onResolved={onSheet} disabled={loading} />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search name, code or email…" className="min-w-[220px] flex-1" />
                <FilterSelect
                  label="Show"
                  icon="rule"
                  value={NEED_FILTERS.find((f) => f.key === need)?.label || 'Everyone'}
                  options={NEED_FILTERS.map((f) => f.label)}
                  onChange={(label) => { setNeed(NEED_FILTERS.find((f) => f.label === label)?.key || 'all'); setPage(1); }}
                />
                <FilterSelect
                  label="Segment"
                  icon="history"
                  value={segment === 'All' ? 'All' : segmentByKey(segment)?.label || segment}
                  options={['All', ...SEGMENTS.map((s) => s.label)]}
                  onChange={(label) => { setSegment(SEGMENTS.find((s) => s.label === label)?.key || 'All'); setPage(1); }}
                />
                {sheet && (
                  <span className="text-[12px] text-cos-ink-secondary">
                    <span className="mono font-semibold text-cos-ink">{sheet.file}</span>: {num(sheet.matched.length)} ticked
                    {sheet.missing.length > 0 && <>, <span className="text-cos-status-amber">{num(sheet.missing.length)} unknown</span></>}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[rgba(250,249,244,0.96)]">
                  <tr className="border-b border-cos-line">
                    <th className="w-10 px-4 py-2.5 text-left">
                      <PageCheckbox all={pageAll} some={pageSome} onChange={togglePage} disabled={rows.length === 0} />
                    </th>
                    {['Vendor', 'Email', 'Missing on record', 'Last link', 'Status'].map((h) => (
                      <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-sm text-cos-ink-secondary">
                        <Icon name="hourglass_top" className="mr-1 align-[-3px] text-[18px] text-cos-teal-deep" />
                        Loading vendors… {all.total ? `${num(all.loaded)} of ${num(all.total)}` : ''}
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-14 text-center text-sm text-cos-ink-muted">No vendors match these filters.</td>
                    </tr>
                  ) : (
                    rows.map((v) => {
                      const ticked = selected.has(v.id);
                      const p = vendorPill(v.status);
                      const link = linkOf.get(v.id);
                      const lp = link ? LINK_STATUS_PILL[link.status] || LINK_STATUS_PILL.sent : null;
                      return (
                        <tr key={v.id} onClick={() => toggle(v)} className={`cursor-pointer border-b border-cos-line-subtle transition-colors last:border-0 ${ticked ? 'bg-[rgba(13,168,155,0.05)]' : 'hover:bg-white/50'}`}>
                          <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" className={CHECKBOX} checked={ticked} onChange={() => toggle(v)} aria-label={`${ticked ? 'Untick' : 'Tick'} ${v.company_name}`} />
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-cos-ink">{v.company_name}</div>
                            <div className="mono text-2xs text-cos-ink-muted">{v.vendor_code || 'No code'} · {MSME_LABEL[v.msme_status] || '—'}</div>
                          </td>
                          <td className="px-3 py-2">
                            {v.email ? (
                              <span className="text-cos-ink-secondary">{v.email}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-cos-status-amber"><Icon name="mail_off" className="text-[14px]" /> No email</span>
                            )}
                          </td>
                          <td className="px-3 py-2"><GapChips inputs={stateOf.get(v.id)?.inputs || []} /></td>
                          <td className="px-3 py-2">
                            {lp ? (
                              <div className="flex flex-col items-start gap-0.5">
                                <StatusPill status={lp.pill} label={lp.label} />
                                <span className="text-2xs text-cos-ink-muted">{formatShortDate(link.at)}</span>
                              </div>
                            ) : (
                              <span className="text-cos-ink-muted">Never</span>
                            )}
                          </td>
                          <td className="px-3 py-2"><StatusPill status={p.pill} label={p.label} /></td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-cos-line-subtle px-5 py-3">
              <Pagination page={safePage} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} noun="vendors" />
            </div>
          </GlassCard>
        )}

        {/* ------------------------------------------------------------- 2 ask */}
        {step === 1 && (
          <GlassCard>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-[15px] font-bold tracking-tight text-cos-ink">What should they send?</h3>
                <p className="mt-0.5 text-[12px] text-cos-ink-secondary">
                  The link opens on just these sections, so nobody is asked to re-enter what you already hold.
                </p>
              </div>
              <Button variant="secondary" size="sm" icon="auto_fix_high" onClick={askWhatIsMissing} disabled={CHECKS.every((c) => neededPerAsk[c.key] === 0)}>
                Ask for what is missing
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {ASKS.map((a) => (
                <AskCard key={a.key} ask={a} selected={asks.has(a.key)} needed={neededPerAsk[a.key] ?? 0} total={selectedRows.length} onToggle={() => toggleAsk(a.key)} disabled={!isAdmin} />
              ))}
            </div>
          </GlassCard>
        )}

        {/* ------------------------------------------------------------- 3 review */}
        {step === 2 && (
          <div className="flex flex-col gap-4">
            <GlassCard padded={false}>
              <div className="px-5 pt-5">
                <h3 className="text-[15px] font-bold tracking-tight text-cos-ink">What each of them receives</h3>
                <p className="mt-0.5 text-[12px] text-cos-ink-secondary">One email, one personal link, valid {LINK_VALID_DAYS} days. No login.</p>
              </div>
              <div className="m-5 mt-3 rounded-xl border border-cos-line bg-white/70">
                <div className="border-b border-cos-line-subtle px-4 py-2.5 text-[12px] text-cos-ink-secondary">
                  <div><span className="font-semibold text-cos-ink">From:</span> Amber Compliance &lt;sos@ambercompliancesystem.com&gt;</div>
                  <div><span className="font-semibold text-cos-ink">Subject:</span> {email.subject}</div>
                </div>
                <div className="px-4 py-3 text-[13px] leading-[19px] text-cos-ink">
                  <p>Dear {'{{vendor name}}'},</p>
                  <p className="mt-2">{email.lead}</p>
                  <ul className="mt-2 flex flex-col gap-1">
                    {email.sections.map((s) => (
                      <li key={s} className="flex items-center gap-1.5"><Icon name="check_circle" className="text-[15px] text-cos-teal-deep" /> {s}</li>
                    ))}
                  </ul>
                  <p className="mt-3">
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-cos-teal px-3 py-1.5 text-[12px] font-semibold text-white">
                      <Icon name="open_in_new" className="text-[14px]" /> Open my vendor form
                    </span>
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard accentRail>
              <div className="flex flex-col gap-3">
                <p className="flex items-start gap-2 text-[13px] text-cos-ink">
                  <Icon name="forward_to_inbox" className="mt-px shrink-0 text-[18px] text-cos-teal-deep" weight={500} />
                  <span>
                    <span className="font-semibold">{num(sendable)} vendor{sendable === 1 ? '' : 's'} will be emailed.</span>{' '}
                    {bySegment.recent > 0 && bySegment.legacy > 0 && <>{num(bySegment.recent)} recent, {num(bySegment.legacy)} legacy. </>}
                    They fill the form themselves; nothing is typed here on their behalf.
                  </span>
                </p>
                {withoutEmail > 0 && (
                  <p className="flex items-start gap-2 text-[13px] text-cos-status-amber">
                    <Icon name="mail_off" className="mt-px shrink-0 text-[18px]" weight={500} />
                    <span><span className="font-semibold">{num(withoutEmail)}</span> of the chosen vendors have no email on record and will not be sent anything.</span>
                  </p>
                )}
                <p className="flex items-start gap-2 text-[13px] text-cos-ink">
                  <Icon name="task_alt" className="mt-px shrink-0 text-[18px] text-cos-teal-deep" weight={500} />
                  <span><span className="font-semibold">No record changes on its own.</span> The form verifies what they enter; you approve the submission.</span>
                </p>
                <label className={`mt-1 flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-[13px] ${isAdmin ? 'cursor-pointer border-cos-line bg-white/60' : 'cursor-not-allowed border-cos-line bg-white/30 text-cos-ink-muted'}`}>
                  <input type="checkbox" className={`${CHECKBOX} mt-0.5`} checked={confirmed} disabled={!isAdmin} onChange={(e) => setConfirmed(e.target.checked)} />
                  <span>
                    I have checked who is being asked and what for, and confirm this email should go to{' '}
                    <span className="mono font-semibold">{num(sendable)}</span> vendor{sendable === 1 ? '' : 's'}.
                    {!isAdmin && <span className="block text-[12px]">Only an admin can send.</span>}
                  </span>
                </label>
              </div>
            </GlassCard>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-1">
          <Button variant="ghost" icon="chevron_left" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || sending}>
            Back
          </Button>
          <div className="flex items-center gap-3">
            {step === 0 && selected.size === 0 && <span className="text-[12px] text-cos-ink-muted">Tick at least one vendor</span>}
            {step === 1 && askKeys.length === 0 && <span className="text-[12px] text-cos-ink-muted">Choose at least one thing to ask for</span>}
            {step < 2 ? (
              <Button variant="primary" iconRight="chevron_right" onClick={() => setStep((s) => s + 1)} disabled={!canContinue}>
                Continue
              </Button>
            ) : (
              <Button variant="primary" icon={sending ? 'hourglass_top' : 'send'} onClick={send} disabled={!canSend}>
                {sending ? 'Sending…' : `Send to ${num(sendable)}`}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------- the rail */}
      <GlassCard className="xl:sticky xl:top-4 xl:self-start">
        <div className="eyebrow mb-2">This send</div>
        <div className={`mono text-[34px] font-bold leading-none tracking-tight ${sendable > 0 ? 'text-cos-ink' : 'text-cos-ink-muted'}`}>{num(sendable)}</div>
        <p className="mt-1.5 text-[12px] text-cos-ink-secondary">
          {selected.size === 0 ? 'Nobody chosen yet.' : <>vendor{sendable === 1 ? '' : 's'} will be emailed</>}
          {withoutEmail > 0 && <span className="mt-1 block text-cos-status-amber">{num(withoutEmail)} without an email cannot be sent.</span>}
        </p>

        <div className="mt-4 border-t border-cos-line-subtle pt-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-semibold text-cos-ink">Asked for</span>
            {askKeys.length > 0 && <span className="mono text-2xs text-cos-ink-muted">~{email.minutes} min each</span>}
          </div>
          {askKeys.length === 0 ? (
            <p className="text-[12px] text-cos-ink-muted">Nothing chosen yet.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {askKeys.map((k) => {
                const a = askByKey(k);
                const t = toneFor(k);
                return (
                  <span key={k} className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold ${t.bg} ${t.text}`}>
                    <Icon name={a.icon} className="text-[14px]" />
                    {a.short}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-2 border-t border-cos-line-subtle pt-3 text-[12px] leading-[17px] text-cos-ink-secondary">
          <p className="flex items-start gap-1.5">
            <Icon name="edit_off" className="mt-px shrink-0 text-[15px] text-cos-teal-deep" />
            Nothing to fill in here — the vendor enters the details.
          </p>
          <p className="flex items-start gap-1.5">
            <Icon name="payments" className="mt-px shrink-0 text-[15px] text-cos-teal-deep" />
            Asking a vendor costs nothing. Only registry checks are charged.
          </p>
          <p className="flex items-start gap-1.5">
            <Icon name="schedule" className="mt-px shrink-0 text-[15px] text-cos-teal-deep" />
            Each link is valid {LINK_VALID_DAYS} days. Unanswered ones can be reminded from the hub.
          </p>
        </div>
      </GlassCard>
    </div>
  );
}

export default ThroughVendorWizard;
